/**
 * Skipp search storefront runtime.
 * Search-page re-rank, analytics, and submit handling.
 * The typed query is kept as-is; suggestions may use a corrected query.
 * Predictive search ranking is handled in predictive-search.js.
 */
(function () {
  'use strict';

  function engine() {
    return window.SkippSearch;
  }

  function processQuery(query) {
    var api = engine();
    if (!api || typeof api.process !== 'function') {
      return {
        original: query,
        shopifyQuery: String(query || '').trim(),
        fallbackQueries: [],
        intent: {},
        corrections: [],
      };
    }
    return api.process(query);
  }

  function searchActionUrl(form) {
    var action = form.getAttribute('action') || '';
    if (action) return action;
    return (window.routes && window.routes.search_url) || '/search';
  }

  function collapseDuplicatedQuery(value) {
    var text = String(value || '')
      .replace(/\s+/g, ' ')
      .trim();
    if (!text) return text;
    var parts = text.split(' ');
    if (parts.length >= 2 && parts.length % 2 === 0) {
      var half = parts.length / 2;
      var left = parts.slice(0, half).join(' ');
      var right = parts.slice(half).join(' ');
      if (left && left === right) return left;
    }
    return text;
  }

  function interceptSearchSubmits() {
    document.addEventListener('submit', function (event) {
      if (event.defaultPrevented) return;
      var form = event.target;
      if (!form || form.tagName !== 'FORM') return;
      var input = form.querySelector('input[type="search"][name="q"]');
      if (!input) return;
      var action = (form.getAttribute('action') || '').toLowerCase();
      if (action && action.indexOf('search') === -1 && form.getAttribute('role') !== 'search') return;

      var original = collapseDuplicatedQuery(input.value);
      if (!original) return;

      event.preventDefault();

      var processed = processQuery(original);
      if (engine()) {
        engine().trackSearch(processed);
      }

      var retrieval = collapseDuplicatedQuery(processed.retrievalQuery || original) || original;

      var params = new URLSearchParams();
      params.set('q', retrieval);
      params.set('options[prefix]', 'last');
      params.set('type', 'product');
      if (retrieval.toLowerCase() !== original.toLowerCase()) {
        params.set('oq', original);
      }

      var url = searchActionUrl(form).split('?')[0];
      window.location.assign(url + '?' + params.toString());
    });
  }

  function searchItemIsSoldOut(li) {
    if (!li) return false;
    if (li.getAttribute('data-available') === 'false') return true;
    if (li.querySelector('[data-skipp-in-stock="false"]')) return true;
    return !!li.querySelector('.card--sold-out');
  }

  function partitionSearchSoldOutLast(items) {
    var avail = [];
    var sold = [];
    (items || []).forEach(function (li) {
      if (searchItemIsSoldOut(li)) sold.push(li);
      else avail.push(li);
    });
    return avail.concat(sold);
  }

  function getSearchPageFromUrl() {
    try {
      var n = parseInt(new URLSearchParams(window.location.search).get('page') || '1', 10);
      return isNaN(n) || n < 1 ? 1 : n;
    } catch (_e) {
      return 1;
    }
  }

  function fetchSearchSectionPage(sectionId, page) {
    var url = new URL(window.location.href);
    if (page <= 1) url.searchParams.delete('page');
    else url.searchParams.set('page', String(page));
    url.searchParams.set('section_id', sectionId);
    return fetch(url.pathname + '?' + url.searchParams.toString(), { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('search section ' + r.status);
      return r.text();
    }).then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      return Array.from(doc.querySelectorAll('#product-grid > li.grid__item'));
    });
  }

  function rebuildSearchPagination(nav, total, perPage) {
    if (!nav) return;
    var pages = Math.max(1, Math.ceil(total / perPage));
    var curPage = getSearchPageFromUrl();
    if (curPage > pages) curPage = pages;
    if (pages <= 1) {
      nav.innerHTML = '';
      return;
    }

    function hrefFor(p) {
      var q = new URLSearchParams(window.location.search);
      if (p <= 1) q.delete('page');
      else q.set('page', String(p));
      var s = q.toString();
      return window.location.pathname + (s ? '?' + s : '');
    }

    function pageNums() {
      var out = [];
      var windowSize = 2;
      var i;
      var last = 0;
      for (i = 1; i <= pages; i++) {
        if (i === 1 || i === pages || (i >= curPage - windowSize && i <= curPage + windowSize)) {
          if (last && i - last > 1) out.push(null);
          out.push(i);
          last = i;
        }
      }
      return out;
    }

    var html =
      '<div class="pagination-wrapper" data-page="' +
      curPage +
      '"><nav class="pagination" role="navigation" aria-label="Pagination"><ul class="pagination__list list-unstyled" role="list">';
    if (curPage > 1) {
      html +=
        '<li><a href="' +
        hrefFor(curPage - 1) +
        '" class="pagination__item pagination__item-arrow pagination__item--arrow-prev link motion-reduce" rel="prev" aria-label="Previous page"><span class="pagination__item-arrow__glyph" aria-hidden="true">&lsaquo;</span></a></li>';
    }
    pageNums().forEach(function (n) {
      if (n === null) {
        html += '<li><span class="pagination__item pagination__item--ellipsis" aria-hidden="true">&hellip;</span></li>';
        return;
      }
      if (n === curPage) {
        html +=
          '<li><span class="pagination__item pagination__item--current" aria-current="page">' +
          n +
          '</span></li>';
      } else {
        html +=
          '<li><a href="' +
          hrefFor(n) +
          '" class="pagination__item link" aria-label="Page ' +
          n +
          '">' +
          n +
          '</a></li>';
      }
    });
    if (curPage < pages) {
      html +=
        '<li><a href="' +
        hrefFor(curPage + 1) +
        '" class="pagination__item pagination__item-arrow pagination__item--arrow-next link motion-reduce" rel="next" aria-label="Next page"><span class="pagination__item-arrow__glyph" aria-hidden="true">&rsaquo;</span></a></li>';
    }
    html += '</ul></nav></div>';
    nav.innerHTML = html;
  }

  function renderSearchSortedPage(wrap, items) {
    var grid = wrap.querySelector('#product-grid') || document.getElementById('product-grid');
    if (!grid) return;
    var perPage = parseInt(wrap.getAttribute('data-skipp-search-per-page') || '36', 10) || 36;
    wrap._skippSearchSortedItems = items;
    var page = getSearchPageFromUrl();
    var pages = Math.max(1, Math.ceil(items.length / perPage));
    if (page > pages) page = pages;
    var start = (page - 1) * perPage;
    var slice = items.slice(start, start + perPage);
    grid.innerHTML = '';
    var frag = document.createDocumentFragment();
    slice.forEach(function (li) {
      frag.appendChild(li);
    });
    grid.appendChild(frag);
    wrap.setAttribute('data-skipp-search-soldout-applied', '1');
    rebuildSearchPagination(wrap.querySelector('[data-skipp-search-pagination]'), items.length, perPage);
  }

  function bindSearchPagination(wrap) {
    if (!wrap || wrap.dataset.skippSearchPaginationBound === '1') return;
    wrap.dataset.skippSearchPaginationBound = '1';
    wrap.addEventListener('click', function (e) {
      var a = e.target.closest('[data-skipp-search-pagination] a.pagination__item[href]');
      if (!a || !wrap._skippSearchSortedItems) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      try {
        var linkUrl = new URL(a.getAttribute('href'), window.location.href);
        history.pushState({ skippSearch: true }, '', linkUrl.pathname + linkUrl.search);
      } catch (_err) {
        window.location.href = a.href;
        return;
      }
      renderSearchSortedPage(wrap, wrap._skippSearchSortedItems);
      wrap.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
  }

  function rerankSearchPage() {
    var api = engine();
    if (!document.body || !document.body.classList.contains('template-search')) return;

    var wrap = document.getElementById('ProductGridContainer');
    var grid = document.getElementById('product-grid');
    if (!wrap || !grid) return;

    var url = new URL(window.location.href);
    var originalQuery = collapseDuplicatedQuery(url.searchParams.get('oq') || url.searchParams.get('q') || '');
    var parsed = processQuery(originalQuery);
    var currentPage = getSearchPageFromUrl();
    var sectionId = wrap.getAttribute('data-skipp-search-section-id') || grid.getAttribute('data-id') || '';
    var pagesHint = parseInt(wrap.getAttribute('data-skipp-search-pages') || '1', 10) || 1;
    var firstItems = Array.from(grid.querySelectorAll(':scope > li.grid__item'));
    if (!firstItems.length) return;

    function orderItems(items) {
      var sortBy = url.searchParams.get('sort_by') || '';
      if (sortBy && sortBy !== 'relevance') return partitionSearchSoldOutLast(items);
      if (!api || typeof api.rankProducts !== 'function') return partitionSearchSoldOutLast(items);
      var products = items.map(function (li, index) {
        return {
          id: li.getAttribute('data-product-id') || '',
          title: li.getAttribute('data-product-title') || '',
          vendor: li.getAttribute('data-product-vendor') || '',
          type: li.getAttribute('data-product-type') || '',
          tags: li.getAttribute('data-product-tags') || '',
          gender: li.getAttribute('data-product-gender') || '',
          available: li.getAttribute('data-available') !== 'false',
          shopifyIndex: index,
          boosted: index < 8,
          boostIndex: index,
          node: li,
        };
      });
      var ranked = api.rankProducts(products, parsed);
      var rankedNodes = [];
      var seen = Object.create(null);
      ranked.forEach(function (product) {
        if (!product.node || seen[product.id]) return;
        seen[product.id] = true;
        rankedNodes.push(product.node);
      });
      items.forEach(function (li) {
        if (rankedNodes.indexOf(li) === -1) rankedNodes.push(li);
      });
      return partitionSearchSoldOutLast(rankedNodes);
    }

    function finish(items) {
      var sorted = orderItems(items);
      renderSearchSortedPage(wrap, sorted);
      bindSearchPagination(wrap);
      if (api && typeof api.trackSearch === 'function') {
        api.trackSearch(parsed, { resultCount: sorted.length });
      }
    }

    if (!sectionId || pagesHint <= 1) {
      finish(firstItems);
      return;
    }

    var fetches = [];
    var pageNum;
    for (pageNum = 1; pageNum <= pagesHint && pageNum <= 30; pageNum++) {
      if (pageNum === currentPage) continue;
      (function (p) {
        fetches.push(
          fetchSearchSectionPage(sectionId, p).then(function (list) {
            return { page: p, list: list || [] };
          })
        );
      })(pageNum);
    }

    Promise.all(fetches)
      .then(function (pages) {
        var byPage = {};
        byPage[currentPage] = firstItems;
        pages.forEach(function (entry) {
          byPage[entry.page] = entry.list;
        });
        var all = [];
        var i;
        for (i = 1; i <= pagesHint; i++) {
          (byPage[i] || []).forEach(function (li) {
            all.push(li);
          });
        }
        finish(all);
      })
      .catch(function () {
        finish(firstItems);
      });
  }

  function restoreTypedQueryInInputs() {
    try {
      var url = new URL(window.location.href);
      var typed = collapseDuplicatedQuery(url.searchParams.get('oq') || '');
      if (!typed) return;
      document.querySelectorAll('input[type="search"][name="q"]').forEach(function (input) {
        input.value = typed;
      });
    } catch (e) {}
  }

  function maybeRedirectToValidSearch() {
    var api = engine();
    if (!api) return;
    if (!document.body || !document.body.classList.contains('template-search')) return;

    var url = new URL(window.location.href);
    if (url.searchParams.get('skipp_sim') === '1') return;
    if (url.searchParams.get('oq')) return;
    var sortBy = url.searchParams.get('sort_by') || '';
    if (sortBy && sortBy !== 'relevance') return;
    var page = url.searchParams.get('page') || '1';
    if (page !== '1') return;

    var hasFilter = false;
    url.searchParams.forEach(function (_value, key) {
      if (String(key).indexOf('filter.') === 0) hasFilter = true;
    });
    if (hasFilter) return;

    var typed = collapseDuplicatedQuery(url.searchParams.get('oq') || url.searchParams.get('q') || '');
    if (!typed) return;
    var parsed = processQuery(typed);
    var retrieval = collapseDuplicatedQuery(parsed.retrievalQuery || typed);
    var currentQ = collapseDuplicatedQuery(url.searchParams.get('q') || '');
    if (!retrieval || retrieval.toLowerCase() === currentQ.toLowerCase()) return;

    var grid = document.getElementById('product-grid');
    var remaining = grid ? grid.querySelectorAll(':scope > li.grid__item').length : 0;
    var emptyFlag = !!document.querySelector('[data-skipp-search-empty="true"]');
    if (remaining > 0 && !emptyFlag) return;

    url.searchParams.set('q', retrieval);
    url.searchParams.set('oq', typed);
    url.searchParams.set('skipp_sim', '1');
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    window.location.replace(url.pathname + '?' + url.searchParams.toString());
  }

  function bindAnalyticsClicks() {
    var api = engine();
    if (!api) return;

    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      var productLink = target.closest(
        '#product-grid a, #predictive-search-results-products-list a[href*="/products/"]'
      );
      if (!productLink) return;
      var host =
        target.closest('li[data-product-id]') ||
        target.closest('[data-product-id]') ||
        target.closest('#product-grid li');
      api.trackProductClick({
        id: host && host.getAttribute('data-product-id'),
      });
    });

    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || !form.getAttribute) return;
      if ((form.getAttribute('action') || '').indexOf('/cart/add') === -1) return;
      var idInput = form.querySelector('input[name="id"]');
      api.trackAddToCart({ id: idInput && idInput.value });
    });
  }

  function collapseDuplicatedUrlAndInputs() {
    try {
      var url = new URL(window.location.href);
      var allQ = url.searchParams.getAll('q');
      var raw = allQ.join(' ');
      var collapsed = collapseDuplicatedQuery(raw);
      var needsRedirect =
        allQ.length > 1 || (collapsed && raw && collapsed !== raw.replace(/\s+/g, ' ').trim());
      if (needsRedirect && collapsed) {
        url.searchParams.delete('q');
        url.searchParams.set('q', collapsed);
        window.location.replace(url.pathname + '?' + url.searchParams.toString());
        return;
      }
      var typed = collapseDuplicatedQuery(url.searchParams.get('oq') || '');
      var display = typed || collapsed;
      document.querySelectorAll('input[type="search"][name="q"]').forEach(function (input) {
        if (display && input.value !== display) input.value = display;
      });
    } catch (e) {}
  }

  function loadCatalog() {
    var api = engine();
    if (!api || typeof api.mergeCatalog !== 'function') return;
    var el = document.getElementById('skipp-search-catalog');
    if (!el) return;
    try {
      var payload = JSON.parse(el.textContent || '{}');
      var extra = (payload.handles || []).map(function (handle) {
        return String(handle || '').replace(/-/g, ' ');
      });
      api.mergeCatalog({
        brands: payload.brands || [],
        collections: payload.collections || [],
        types: payload.types || [],
        titles: payload.titles || [],
        extra: extra,
      });
    } catch (e) {}
  }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  interceptSearchSubmits();
  bindAnalyticsClicks();
  onReady(function () {
    loadCatalog();
    collapseDuplicatedUrlAndInputs();
    restoreTypedQueryInInputs();
    rerankSearchPage();
    maybeRedirectToValidSearch();
  });
  window.addEventListener('popstate', function () {
    var wrap = document.getElementById('ProductGridContainer');
    if (!wrap || !wrap._skippSearchSortedItems) return;
    renderSearchSortedPage(wrap, wrap._skippSearchSortedItems);
  });

  window.SkippSearchRuntime = {
    processQuery: processQuery,
    rerankSearchPage: rerankSearchPage,
  };
})();
